import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../interceptor/axiosInstance";
import { Link, useNavigate } from "react-router-dom";
import NotificationService from "../Utils/NotificationService";
import countryCodes from "../Utils/CountryCodes.json"; // Import country codes
import "./Register.css";

const OrganizationSignup = () => {
  const navigate = useNavigate();
  const [data, setData] = useState("");
  const [error, setError] = useState(null);
  const [isSignupSuccessful, setIsSignupSuccessful] = useState(false);
  const [successEmail, setSuccessEmail] = useState("");

  const formik = useFormik({
    initialValues: {
      name: "",
      organisationName: "",
      emailId: "",
      countryCode: "+91", // Default India
      mobileNumber: "",
      websiteUrl: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .max(50, "Must be 50 characters or less"),
      organisationName: Yup.string()
        .required("Organization Name is required")
        .max(50, "Must be 100 characters or less"),
      emailId: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      mobileNumber: Yup.string()
        .required("Phone Number is required")
        .matches(/^\d{10}$/, "Must be a valid 10-digit phone number"),
      websiteUrl: Yup.string().required("Website URL is required"),
      password: Yup.string()
      .required("Password is required")
      .min(6, "Password must be at least 6 characters long")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/\d/, "Password must contain at least one number"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password is required"),
    }),

    onSubmit: async (payload, { resetForm }) => {
      console.log("Form Submitted:", payload);
      try {
        const response = await axiosInstance.post(`/auth/sign-up`, payload);
        console.log(response.data)
        if(response?.data?.error === "ALREADY EXIST") {
          NotificationService.error("An organization with the provided email address already exists.");
        } else {
          setSuccessEmail(payload.emailId);
          setData(response.data); // Handle response data
          setError(null); // Clear any previous errors
          resetForm();
          setIsSignupSuccessful(true);
        }
      } catch (err) {
        if (err.response?.data?.error === "ALREADY EXIST") {
          NotificationService.error("An organization with the provided email address already exists.");
        }
        setError(err.message);
        setData("");
      }
    },
  });

  if (isSignupSuccessful) {
    return (
      <div className="auth-page">
        <div className="bg-orbs">
          <div className="orb-1"></div>
          <div className="orb-2"></div>
        </div>
        <div className="auth-card">
          <Link to="/">
            <img src="/ezzDesk_Logo.svg" alt="EzzDesk Logo" className="auth-logo" />
          </Link>
          <h2 className="auth-title" style={{color: '#179e24'}}>Signup Successful!</h2>
          <p style={{color: '#475569', marginBottom: '20px'}}>
            Your organization has been registered successfully. You can
            now log in and start using our services.
          </p>
          <p style={{color: '#475569', marginBottom: '30px', fontWeight: '500'}}>
            Please confirm your email from the link sent in the welcome email to {successEmail}
          </p>
          <Link to="/login" className="auth-button" style={{display: 'inline-block', textDecoration: 'none'}}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="bg-orbs">
        <div className="orb-1"></div>
        <div className="orb-2"></div>
      </div>
      
      <div className="auth-card">
        <Link to="/">
          <img src="/ezzDesk_Logo.svg" alt="EzzDesk Logo" className="auth-logo" />
        </Link>
        <h1 className="auth-title">Create a completely free account</h1>
        
        <form onSubmit={formik.handleSubmit} className="auth-form" noValidate>
          
          <div>
            <input
              id="name"
              name="name"
              type="text"
              placeholder="Enter Name"
              className="auth-input"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name && (
              <span className="text-danger">{formik.errors.name}</span>
            )}
          </div>

          <div>
            <input
              id="organisationName"
              name="organisationName"
              type="text"
              placeholder="Enter Organisation Name"
              className="auth-input"
              value={formik.values.organisationName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.organisationName && formik.errors.organisationName && (
              <span className="text-danger">{formik.errors.organisationName}</span>
            )}
          </div>

          <div>
            <input
              id="emailId"
              name="emailId"
              type="email"
              placeholder="Enter Email Id"
              className="auth-input"
              value={formik.values.emailId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.emailId && formik.errors.emailId && (
              <span className="text-danger">{formik.errors.emailId}</span>
            )}
          </div>

          <div className="form-row">
            <div style={{ flex: '0 0 120px' }}>
              <select
                id="countryCode"
                name="countryCode"
                className="auth-select"
                value={`${formik.values.countryCode}-${formik.values.countryName || ''}`}
                onChange={(e) => {
                  const [code, name] = e.target.value.split('-'); 
                  formik.setFieldValue("countryCode", code);
                  formik.setFieldValue("countryName", name);
                }}
                onBlur={formik.handleBlur}
              >
                {countryCodes.map((country) => (
                  <option 
                    key={`${country.code}-${country.name}`} 
                    value={`${country.code}-${country.name}`}
                  >
                    {country.flag} {country.code} ({country.name})
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ flex: '1' }}>
              <input
                id="mobileNumber"
                name="mobileNumber"
                type="text"
                placeholder="Enter Mobile Number"
                className="auth-input"
                value={formik.values.mobileNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                <span className="text-danger">{formik.errors.mobileNumber}</span>
              )}
            </div>
          </div>

          <div>
            <input
              id="websiteUrl"
              name="websiteUrl"
              type="text"
              placeholder="Enter Website URL"
              className="auth-input"
              value={formik.values.websiteUrl}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.websiteUrl && formik.errors.websiteUrl && (
              <span className="text-danger">{formik.errors.websiteUrl}</span>
            )}
          </div>

          <div>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Enter Password"
              className="auth-input"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.password && formik.errors.password && (
              <span className="text-danger">{formik.errors.password}</span>
            )}
          </div>

          <div>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Enter Confirm Password"
              className="auth-input"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <span className="text-danger">{formik.errors.confirmPassword}</span>
            )}
          </div>

          <div style={{ fontSize: '13px', color: '#475569', marginTop: '-5px' }}>
            <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
              <li>6 characters minimum</li>
              <li>At least one number</li>
              <li>Upper & lowercase character</li>
            </ul>
          </div>

          <button type="submit" className="auth-button">
            Sign Up for free
          </button>
        </form>
        
        <p className="auth-footer">
          Already have an account? <Link to="/login" className="auth-link">Return to Sign In</Link>
        </p>
      </div>
    </div>
  );
};

export default OrganizationSignup;